import React from 'react';
import SafeIcon from '../../common/SafeIcon';

const NewsCard = ({ news }) => {
  const handleShare = async () => {
    const shareData = {
      title: news.hook || news.title,
      text: news.summary,
      url: window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copied to clipboard!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };

  // Format comma-separated categories nicely for display
  const displayCategory = (news.category || 'General').split(',').map(c => c.trim()).join(' · ');

  return (
    <article className="editorial-card p-5 w-full max-w-[800px] mx-auto flex flex-col gap-4">
      {/* Header / Meta */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-[13px] md:text-[14px] font-mono text-brand-secondary uppercase tracking-widest">
          <span className="font-bold text-brand-text">{displayCategory}</span>
          <span className="text-brand-borderHover">•</span>
          <span>{news.date}</span>
        </div>
        <div className="flex gap-4 text-brand-muted">
          <button onClick={handleShare} className="hover:text-brand-text transition-colors pointer-events-auto" aria-label="Share">
            <SafeIcon icon="Share2" className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div>
        <h2 className="text-[26px] md:text-[32px] lg:text-[36px] font-bold text-brand-text leading-tight mb-2">
          {news.hook}
        </h2>
        <p className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55]">
          {news.summary}
        </p>
      </div>

      {/* Points */}
      {news.points && news.points.length > 0 && news.points[0] !== "" && (
        <div className="mt-2">
          <ul className="space-y-2">
            {news.points.map((point, idx) => point ? (
              <li key={idx} className="flex items-start gap-2.5 text-[18px] md:text-[20px] text-brand-secondary leading-[1.55]">
                <SafeIcon icon="ArrowRight" className="w-4 h-4 text-brand-mint mt-[6px] flex-shrink-0" />
                <span>{point}</span>
              </li>
            ) : null)}
          </ul>
        </div>
      )}

      {/* Deep Links Section */}
      {news.deepLinks && news.deepLinks.length > 0 && (
        <div className="mt-3 grid gap-2 pointer-events-auto">
          {news.deepLinks.map((link, idx) => (
            <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-brand-bg border border-brand-border rounded-[8px] hover:border-brand-mint transition-colors group">
              {link.thumbnail && (
                <img src={link.thumbnail} alt={link.title} className="w-10 h-10 rounded-[6px] object-cover bg-brand-surface flex-shrink-0" />
              )}
              <div className="flex items-center gap-2">
                {!link.thumbnail && <SafeIcon icon="Link" className="w-4 h-4 text-brand-muted group-hover:text-brand-mint transition-colors" />}
                <span className="text-[14px] font-bold text-brand-text group-hover:text-brand-mint transition-colors line-clamp-2">
                  {link.title || 'Visit Link'}
                </span>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Why it matters */}
      {news.whyItMatters && (
        <div className="mt-2 pt-4 border-t border-brand-border">
          <h3 className="text-[18px] md:text-[20px] font-bold text-brand-text mb-1">Why it matters</h3>
          <p className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55]">{news.whyItMatters}</p>
        </div>
      )}

      {/* Main Call To Action */}
      {news.ctaText && news.ctaUrl && (
        <a href={news.ctaUrl} target="_blank" rel="noopener noreferrer" className="mt-2 block w-full py-3 bg-brand-mint text-brand-bg text-center text-[15px] font-bold rounded-[8px] hover:opacity-90 transition-opacity pointer-events-auto">
          {news.ctaText}
        </a>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-2 pt-4 border-t border-brand-border text-[13px] md:text-[14px] font-mono text-brand-muted">
        <a href="#" className="flex items-center gap-1.5 hover:text-brand-mint transition-colors pointer-events-auto">
          <SafeIcon icon="ExternalLink" className="w-3.5 h-3.5" />
          {news.source || 'Direct Source'}
        </a>
        <span className="flex items-center gap-1.5">
          <SafeIcon icon="Clock" className="w-3.5 h-3.5" />
          {news.readTime || '1 min'}
        </span>
      </div>
    </article>
  );
};

export default NewsCard;